import PropTypes from 'prop-types'
import React from 'react'
import { FormSpy, Field } from 'react-final-form'

import Checkbox from 'components/_form/Checkbox'
import PhoneWithCode from 'components/_form/PhoneWithCode'
import TextField from 'components/_form/TextField'
import CheckboxTerms from 'components/_special/CheckboxTerms'
import OrderFieldInfo from 'components/_special/OrderFieldInfo'
import { DELIVERY_TYPES } from 'constants/sales'
import { useDeliveryTypeAnimation } from 'forms/hooks/useDeliveryTypeAnimation'
import useLoqateSearch from 'forms/hooks/useLoqateSearch'
import {
  composeValidators,
  errorFromDict,
  errorCustom,
  required,
  isEmail,
} from 'forms/validations'
import { Row, Col } from 'styles/components/layout'

import { RowStyled, ColStyled, CheckboxWrapper } from './styles'

const addressFields = [
  {
    name: 'postcode',
    textName: 'postcode',
    autoComplete: 'postcode',
    validate: true,
  },
  {
    name: 'address',
    textName: 'address',
    autoComplete: 'address-line1',
    validate: true,
  },
  {
    name: 'address2',
    textName: 'address2',
    autoComplete: 'address-line2',
  },
  {
    name: 'address3',
    textName: 'address3',
    autoComplete: 'address-line3',
  },
  {
    name: 'city',
    textName: 'city',
    autoComplete: 'address-level2',
    validate: true,
  },
  {
    name: 'region',
    textName: 'district',
    autoComplete: 'address-level1',
  },
]

const PersonalInfo = React.forwardRef(
  (
    {
      onChange,
      id,
      errorsDict,
      optionsPhoneCode,
      texts,
      isDisabled,
      deliveryType,
      handleSubmit,

      locale,
      alpha2Code,

      batch,
      change,
      submitting,
    },
    ref,
  ) => {
    const collapsedFields = React.useRef(null)
    /*
     * Memorize validations
     */
    const cityValidation = React.useMemo(() => {
      return deliveryType === DELIVERY_TYPES.courier
        ? errorCustom(errorsDict.contact_details_required, required)
        : null
    }, [deliveryType, errorsDict])

    /*
     * Handle animation for delivery switch
     */
    useDeliveryTypeAnimation({
      dropdownRef: ref,
      collapsedFields,
      deliveryType,
    })

    useLoqateSearch({ alpha2Code, batch, change })

    return (
      <form id={id} onSubmit={handleSubmit} noValidate={true}>
        <div ref={collapsedFields}>
          <RowStyled>
            <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
              <PhoneWithCode
                {...{
                  texts,
                  optionsPhoneCode,
                  isDisabled,
                  errorsDict,
                  locale,
                  alpha2Code,
                }}
              />
            </Col>
            <OrderFieldInfo>{texts.fields.phone.hint}</OrderFieldInfo>
          </RowStyled>

          <RowStyled>
            <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
              <TextField
                autoComplete="email"
                placeholder={texts.fields.email.title}
                type="email"
                name="email"
                isDisabled={isDisabled}
                validate={composeValidators([
                  errorCustom(errorsDict.contact_details_required, required),
                  errorFromDict(errorsDict, isEmail),
                ])}
              />
            </Col>
            <OrderFieldInfo>{texts.fields.email.hint}</OrderFieldInfo>
          </RowStyled>

          <RowStyled>
            <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
              <TextField
                autoComplete="name"
                placeholder={texts.fields.name.title}
                name="name"
                isDisabled={isDisabled}
                validate={errorCustom(
                  errorsDict.contact_details_required,
                  required,
                )}
              />
            </Col>
            <OrderFieldInfo>{texts.fields.name.hint}</OrderFieldInfo>
          </RowStyled>

          {!!texts.fields.company.title && (
            <RowStyled>
              <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
                <TextField
                  autoComplete="organization"
                  placeholder={texts.fields.company.title}
                  name="company"
                  isDisabled={isDisabled}
                />
              </Col>
              <OrderFieldInfo>{texts.fields.company.hint}</OrderFieldInfo>
            </RowStyled>
          )}

          <RowStyled>
            <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
              <TextField
                key={deliveryType}
                placeholder={texts.fields.find_address.title}
                autoComplete="new-password"
                name="find-address"
                isDisabled={isDisabled}
                onChange={() => {
                  batch(() => {
                    change('postcode', '')
                    change('address', '')
                    change('address2', '')
                    change('address3', '')
                    change('region', '')
                    change('city', '')
                  })
                }}
              />
            </Col>
            <OrderFieldInfo>{texts.fields.find_address.hint}</OrderFieldInfo>
          </RowStyled>

          {addressFields.map(
            field =>
              !!texts.fields[field.textName].title && (
                <RowStyled key={field.name}>
                  <Col width={{ xs: 1, md: 7 / 12, lg: 1 / 2 }}>
                    <TextField
                      key={deliveryType}
                      placeholder={texts.fields[field.textName].title}
                      autoComplete={field.autoComplete}
                      name={field.name}
                      isDisabled={isDisabled}
                      validate={field.validate ? cityValidation : null}
                    />
                  </Col>
                  <OrderFieldInfo>
                    {texts.fields[field.textName].hint}
                  </OrderFieldInfo>
                </RowStyled>
              ),
          )}
        </div>

        <Row>
          <ColStyled width={{ xs: 1 }}>
            <CheckboxWrapper>
              <Checkbox
                name="is_offer_accepted"
                isDisabled={isDisabled}
                label={
                  <CheckboxTerms
                    locale={locale}
                    agreement={texts.fields.agreement}
                    isOldPattern={true}
                  />
                }
                validate={errorCustom(errorsDict.have_to_agree, required)}
              />
            </CheckboxWrapper>
          </ColStyled>
        </Row>

        <Field
          name="submit"
          type="submit"
          component="input"
          hidden={true}
          disabled={submitting || isDisabled}
        />

        <FormSpy
          subscription={{
            values: true,
            invalid: true,
            active: true,
            touched: true,
          }}
          onChange={({ values, invalid, active, touched }) =>
            onChange({ values, invalid, active, touched })
          }
        />
      </form>
    )
  },
)

PersonalInfo.propTypes = {
  errorsDict: PropTypes.object.isRequired,
  id: PropTypes.string.isRequired,
  optionsPhoneCode: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }),
  ).isRequired,
  texts: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  deliveryType: PropTypes.number.isRequired,

  locale: PropTypes.string.isRequired,
  alpha2Code: PropTypes.string.isRequired,

  handleSubmit: PropTypes.func.isRequired,
  batch: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  submitting: PropTypes.bool.isRequired,
}

export default PersonalInfo
